import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { FormControl } from "@material-ui/core";

import { getChangedData, getDefaultWidgetName, getTagid } from "utils/utils";
import { DROPDOWN_STRINGS, InputErrorStrings, PredictTagStrings, valueStrings, WidgetStrings } from "constants/constantStrings";
import { WIDGET_TITLE_INPUT_MAXIMUM } from "constants/globalConstants";
import { WIDGET_TYPES } from "constants/widgets.type.const";

import CustomModal from "components/CustomModal";
import Dropdown from "components/Dropdown";
import InputBox from "components/InputBox";
import TagDropdown from "components/TagDropdown";

import PREDICTION_FORM_CONST from "./PredictionForm.const";

import "./index.css";

export default function PredictionWidgetForm(props) {
  const { WATER_PREDICTION_TAGS, CHEMICAL_PREDICTION_TAGS, PREDICTION_TIMES, PREDICTION_MODELS, PREDICTION_DURATION } = PREDICTION_FORM_CONST;
  const { Header, SubType, Attributes, currentLayout, setIsReportRerendering, isEdit, onWidgetAction } = props.data;
  const { SystemTag, Duration } = Attributes || {};
  const [tagsOption, setTagsOption] = useState([]);
  const [modelTypeError, setModeTypeError] = useState();
  const { tagsAsObj } = useSelector(state => state.tags);

  useEffect(() => {
    setWidgetData(getWidgetDefaultState());
    setTagOptions(SubType);
  }, [props.data]);

  const getWidgetDefaultState = () => ({
    Header: Header || getDefaultWidgetName(),
    SubType: PREDICTION_MODELS.find(model => model.name === SubType),
    Attributes: {
      SystemTag: tagsAsObj[SystemTag] || null,
      Duration: Duration || PREDICTION_DURATION.ONE_DAY,
      Comments: Attributes?.Comments || ""
    }
  });
  const [openModal, setOpenModal] = useState(true);
  const [isFormInvalid, setIsFormInvalid] = useState(true);
  const [widgetData, setWidgetData] = useState(getWidgetDefaultState());

  const closeModal = data => {
    setOpenModal(false);
    props.onClose(data);
  };

  const onTagChange = Tag => validateForm({ ...widgetData, Attributes: { ...widgetData.Attributes, SystemTag: Tag } });
  const onTitleChange = Header => validateForm({ ...widgetData, Header });
  const filterTagList = tagIDs => tagIDs.map(TagId => tagsAsObj[TagId]);

  const onModelChange = PredictionModel => {
    const error = PredictionModel ? null : InputErrorStrings.required;
    setModeTypeError(error);
    const tagsOption = setTagOptions(PredictionModel);
    const Tag = tagsOption.length ? tagsOption[0] : null;
    validateForm({ ...widgetData, Attributes: { ...widgetData.Attributes, SystemTag: tagsOption[0] }, SubType: PredictionModel });
  };

  const setTagOptions = PredictionModel => {
    let tagOptions = [];
    if (PredictionModel) {
      if (PredictionModel.name === PredictTagStrings.chemicalPrediction) {
        tagOptions = filterTagList(CHEMICAL_PREDICTION_TAGS);
      } else {
        tagOptions = filterTagList(WATER_PREDICTION_TAGS);
      }
    }
    setTagsOption(tagOptions);
    return tagOptions;
  };

  const onPredictionTimeChange = event => {
    const Duration = event?.target?.value;
    const tempData = { ...widgetData, Attributes: { ...widgetData.Attributes, Duration } };
    validateForm(tempData);
  };

  const validateForm = formData => {
    const { Header, SubType, Attributes } = formData;
    const { SystemTag, Duration } = Attributes;
    const isFormValid = Header && SystemTag?.TagId && SubType && Duration && Header.trim().length > 0;
    setIsFormInvalid(!isFormValid);
    setWidgetData(formData);
  };

  const getPayload = () => {
    const LayoutID = currentLayout.ID;
    const { Header, SubType, Attributes } = widgetData;
    const { SystemTag, Duration, Comments } = Attributes;
    return {
      Header,
      LayoutID,
      Type: WIDGET_TYPES.PREDICTION,
      SubType: SubType.name,
      Attributes: {
        SystemTag: [SystemTag.TagId],
        Comments: Comments || "",
        Duration
      }
    };
  };
  const getEditTagPaylod = () => {
    let param = getChangedData(widgetData, getWidgetDefaultState());
    const { Attributes, SubType } = param;
    if (Attributes?.SystemTag) param = getTagid(widgetData.Attributes?.SystemTag, param);
    if (SubType) {
      param = { ...param, SubType: SubType.name };
    }
    return { ...param, Id: props.data.Id, LayoutID: currentLayout.ID };
  };
  const onFormSubmit = async () => {
    setIsFormInvalid(true);
    let payload = {};
    if (isEdit) {
      payload = getEditTagPaylod();
    } else {
      payload = getPayload();
      onWidgetAction("ADD_WIDGET", payload);
      setIsFormInvalid(false);
    }
    closeModal(payload);
  };

  return (
    <>
      {openModal && (
        <CustomModal
          show={openModal}
          title={isEdit ? PredictTagStrings.editTitle : PredictTagStrings.addTitle}
          confirmBtnText={isEdit ? WidgetStrings.editWidget : WidgetStrings.addWidget}
          description={PredictTagStrings.description}
          secondaryDescription={valueStrings.mandatoryField}
          close={closeModal}
          confirmBtnAction={onFormSubmit}
          confirmDisabled={isFormInvalid}
          customClass={"content-height"}
        >
          <div className="widget-form-container">
            <div className="form-group">
              <label className="bodyMainHeadingText" htmlFor="widgetTitle">
                {WidgetStrings.widgetTitle}*
              </label>
              <InputBox
                name="widgetTitle"
                value={widgetData.Header}
                placeholder={WidgetStrings.titlePlaceholder}
                onChange={onTitleChange}
                maxLength={WIDGET_TITLE_INPUT_MAXIMUM}
                requiredMsg={InputErrorStrings.required}
              />
            </div>

            <div className="form-group">
              <Dropdown
                value={widgetData.SubType}
                labelText={`${PredictTagStrings.modelType}*`}
                options={PREDICTION_MODELS}
                errorMsg={modelTypeError}
                placeholder={DROPDOWN_STRINGS.chooseOptionStr}
                getOptionLabel={option => option.name}
                onChange={onModelChange}
              />
            </div>

            <div className="form-group">
              <label className="bodyMainHeadingText">{WidgetStrings.selectTag}*</label>
              <TagDropdown
                tagValue={widgetData.Attributes.SystemTag}
                requiredMsg={InputErrorStrings.required}
                dropDownID="predictionTagDropDown"
                placeholder={DROPDOWN_STRINGS.chooseOptionStr}
                tagList={tagsOption}
                onTagChange={onTagChange}
                disabled={widgetData.SubType?.name === PredictTagStrings.chemicalPrediction}
              />
            </div>

            <FormControl>
              <label className="bodyMainHeadingText">{PredictTagStrings.predictionTime}*</label>
              <div className="radio-group">
                {Object.keys(PREDICTION_TIMES).map((key, idx) => (
                  <label className="radiocontainer" key={idx}>
                    {PREDICTION_TIMES[key]}
                    <input
                      className="radioLabel"
                      type="radio"
                      checked={widgetData.Attributes.Duration === key}
                      value={key}
                      onChange={onPredictionTimeChange}
                      name="levelType"
                    />
                    <span className="radioButton" />
                  </label>
                ))}
              </div>
            </FormControl>
          </div>
        </CustomModal>
      )}
    </>
  );
}
